import { useState, useEffect, useContext, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Card,
  Grid,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Box,
  Icon,
  Switch,
  TextField,
} from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import QrCodeIcon from "@mui/icons-material/QrCode2";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";

import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import axiosInstance from "api/axiosInstance";
import { getUserProfile, updateUserProfile } from "../../../../api/userApi";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS } from "theme/tokens";
import { fmtDate } from "utils/datetime";

// ─────────────────────────────────────────────────────────────
// Plan tag 樣式（與 PlanUpgradeManager 一致）
// ─────────────────────────────────────────────────────────────
const PLAN_TAG_STYLE = {
  STARTER: { bg: COLORS.black, color: COLORS.white },
  FREE: { bg: COLORS.grayDark, color: COLORS.white },
  GROWTH: { bg: COLORS.main, color: COLORS.white },
  PRO: { bg: COLORS.purple, color: COLORS.white },
  FREE_TRIAL: { bg: COLORS.black, color: COLORS.white },
  BRONZE: { bg: COLORS.main, color: COLORS.white },
  SILVER: { bg: COLORS.grayDark, color: COLORS.white },
  GOLD: { bg: COLORS.purple, color: COLORS.white },
};

const PRO_LIKE_CODES = ["PRO", "GOLD"];                 // 不顯示「升級方案」按鈕
const TRIAL_LIKE_CODES = ["STARTER", "FREE_TRIAL"];       // 顯示「30 天試用中」黑 tag
const NO_AUTORENEW_CODES = ["STARTER", "FREE_TRIAL", "FREE"]; // 自動續費 disabled

// 灰底 filled input 樣式（對齊截圖一、登入頁同款）
const FILLED_INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: COLORS.grayLightest,
    borderRadius: 1.5,
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused": {
      backgroundColor: COLORS.white,
      "& fieldset": { border: "1px solid", borderColor: "info.main" },
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "12px 14px",
    fontSize: 14,
  },
};

export default function EditProfile() {
  const navigate = useNavigate();
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);

  useEffect(() => {
    loadLanguageFile("EditProfile", "profile/EditProfile/EditProfile_lang");
  }, [language, loadLanguageFile]);

  const LANGUAGE = useMemo(
    () => componentLanguageData.EditProfile || { Main: {}, Alert: {}, Error: {} },
    [componentLanguageData]
  );
  const L = LANGUAGE.Main || {};

  const qrRef = useRef(null);

  const [profile, setProfile] = useState({
    fullName: "",
    email: "",
    planCode: "",
    planName: "",
    referralCode: "",
    accountStatus: "ACTIVE",
  });
  const [autoRenewEnabled, setAutoRenewEnabled] = useState(false);
  const [billingCycle, setBillingCycle] = useState(null); // monthly | yearly | null（年繳點自動續費引導用）
  // 2026-09-28 月繳期滿：/plans/me/plan 的 contract（{ isFinalPeriod, endsAt, execTimes }）；最後一期時開關換成期滿日＋立即重新訂閱
  const [contract, setContract] = useState(null);
  // 2026-05-25 關閉自動續費確認 modal
  const [confirmOffOpen, setConfirmOffOpen] = useState(false);
  const [autoRenewLoading, setAutoRenewLoading] = useState(false);
  const [effectiveUntil, setEffectiveUntil] = useState(null);
  // 2026-05-25 綠界已取消 → 立即重新訂閱 modal
  const [resubscribeOpen, setResubscribeOpen] = useState(false);
  const [resubMode, setResubMode] = useState("canceled"); // canceled＝綠界已取消／contract＝月繳期滿（最後一期）
  const [yearlyNoticeOpen, setYearlyNoticeOpen] = useState(false); // 年繳點自動續費 → 正式彈窗

  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [qrOpen, setQrOpen] = useState(false);

  // 抓使用者基本資料
  useEffect(() => {
    (async () => {
      try {
        const res = await getUserProfile();
        const user = res.data.user || res.data;
        setProfile({
          fullName: user.fullName || "",
          email: user.email || "",
          planCode: user.planCode || "",
          planName: user.plan?.name || "",
          referralCode: user.referralCode || "",
          accountStatus: user.accountStatus || "ACTIVE",
        });
      } catch (err) {
        console.error(LANGUAGE.Error?.Content1 || "❌ 無法取得使用者資料", err);
      }
    })();
  }, [LANGUAGE.Error]);

  // 抓 plan / autoRenew 狀態
  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get("/plans/me/plan");
        setAutoRenewEnabled(!!res.data?.autoRenewEnabled);
        setBillingCycle(res.data?.billingCycle || null);
        setContract(res.data?.contract || null);
      } catch (err) {
        console.error("無法取得方案狀態", err);
      }
    })();
  }, []);

  // 2026-05-25 自動續費 toggle 互動
  //  - 開：直接呼叫 API 開啟（綠界仍在 → 無縫續扣）
  //  - 關：先跳確認 modal，確認後才呼叫 API
  const handleAutoRenewChange = async (e) => {
    const next = e.target.checked;
    // 2026-06-08：試用/免費方案（STARTER/FREE_TRIAL/FREE）沒有可續訂的訂閱，引導升級而非顯示「設定失敗」
    if (isNoAutoRenew) {
      toast.info(L.AutoRenewRequireUpgrade || "如要開啟自動續費，請先升級為付費會員方案。");
      return;
    }
    // 2026-06-13：年繳為一次性付款、無 recurring mandate，無法開啟月繳自動續費 → 跳正式彈窗而非重訂閱
    if (next && billingCycle === "yearly") {
      setYearlyNoticeOpen(true);
      return;
    }
    if (!next) {
      // 要關閉 → 先抓到期日，再跳確認 modal
      try {
        const res = await axiosInstance.get("/users/subscription/auto-renew");
        setEffectiveUntil(res.data?.nextBillingDate || null);
      } catch (_) {
        setEffectiveUntil(null);
      }
      setConfirmOffOpen(true);
      return;
    }
    // 要開啟
    setAutoRenewLoading(true);
    try {
      await axiosInstance.post("/users/subscription/auto-renew", { enabled: true });
      setAutoRenewEnabled(true);
      toast.success(L.AutoRenewOnToast || "已開啟自動續費");
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === "ALREADY_CANCELED_AT_GATEWAY") {
        // 綠界已被取消、無法無縫恢復 → 跳「立即重新訂閱」modal（友善版，不讓他等到期）
        setResubMode("canceled");
        setResubscribeOpen(true);
      } else if (code === "CONTRACT_ENDING") {
        // 2026-09-28：已是月繳最後一期，開了也不會再扣款 → 期滿版的重新訂閱 modal
        setResubMode("contract");
        setResubscribeOpen(true);
      } else {
        toast.error(L.AutoRenewError || "設定失敗，請稍後再試");
      }
    } finally {
      setAutoRenewLoading(false);
    }
  };

  // 2026-05-25 立即重新訂閱（複用升級訂閱金流，webhook 會退舊單剩餘 Coin）
  const confirmResubscribe = async () => {
    setAutoRenewLoading(true);
    try {
      const res = await axiosInstance.post("/plans/upgrade-subscribe", {
        planCode: profile.planCode,
      });
      const popup = window.open();
      if (popup) popup.document.write(res.data);
      setResubscribeOpen(false);
    } catch (err) {
      toast.error(L.AutoRenewError || "設定失敗，請稍後再試");
    } finally {
      setAutoRenewLoading(false);
    }
  };

  const confirmTurnOffAutoRenew = async () => {
    setAutoRenewLoading(true);
    try {
      await axiosInstance.post("/users/subscription/auto-renew", { enabled: false });
      setAutoRenewEnabled(false);
      setConfirmOffOpen(false);
      toast.success(L.AutoRenewOffToast || "已關閉自動續費");
    } catch (err) {
      toast.error(L.AutoRenewError || "設定失敗，請稍後再試");
    } finally {
      setAutoRenewLoading(false);
    }
  };

  const code = profile.planCode || "FREE";
  const tagStyle = PLAN_TAG_STYLE[code] || PLAN_TAG_STYLE.FREE;
  const isTrial = TRIAL_LIKE_CODES.includes(code);           // 只判 TRIAL/FREE_TRIAL
  const isNoAutoRenew = NO_AUTORENEW_CODES.includes(code);    // 含 FREE
  const isFinalPeriod = !isNoAutoRenew && !!contract?.isFinalPeriod; // 2026-09-28 月繳期滿（最後一期）
  const isPro = PRO_LIKE_CODES.includes(code);
  const rawTagLabel = isTrial
    ? (L.TagTrialActive || "Starter Plan（體驗版）")
    : code === "FREE"
      ? (L.PlanFree || "Free")
      : code === "STARTER"
        ? (L.PlanStarter || "Starter")
        : code === "GROWTH"
          ? (L.PlanGrowth || "Growth")
          : code === "PRO"
            ? (L.PlanPro || "Pro")
            : (profile.planName || code);
  // 顯示「目前方案」用簡潔名：去掉（中文說明）/(英文說明) 括號
  const tagLabel = (rawTagLabel || "").replace(/（[^）]*）/g, "").replace(/\([^)]*\)/g, "").trim();

  // 推薦碼 / 推薦網址
  const referralUrl = profile.referralCode
    ? `${(L.Content13 || "https://trade.masquant.com/authentication/sign-in?ref=")}${profile.referralCode}`
    : "";

  const handleEditClick = () => {
    setEditName(profile.fullName);
    setEditOpen(true);
  };

  const handleEditConfirm = async () => {
    try {
      await updateUserProfile({ fullName: editName });
      setProfile((p) => ({ ...p, fullName: editName }));
      toast.success(LANGUAGE.Alert?.Content1 || "更新成功！");
      setEditOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const copyToClipboard = (text, msg) => {
    navigator.clipboard.writeText(text);
    toast.success(msg);
  };

  return (
    <MDBox pt={2} pb={1}>
      <Grid container spacing={3}>
        {/* ───── 左卡：會員資料 ───── */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, borderRadius: 3, boxShadow: 1, height: "100%", width: "100%" }}>
            {/* 標題列 + 編輯按鈕 */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 4, height: 18, bgcolor: COLORS.main, borderRadius: 1 }} />
                <MDTypography variant="subtitle1" fontWeight="bold">
                  {L.FieldName || "名稱："}{profile.fullName || "—"}
                </MDTypography>
              </Box>
              <MDButton
                variant="outlined"
                size="small"
                onClick={handleEditClick}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  borderColor: COLORS.grayMedian,
                  color: COLORS.black,
                  "&:hover": { borderColor: COLORS.grayMedian },
                }}
              >
                {L.ActionEdit || "編輯"}
              </MDButton>
            </Box>

            {/* Plan tag — 加大 padding + 更圓角，對齊截圖一視覺份量 */}
            <Box mb={2}>
              <Box
                sx={{
                  display: "inline-block",
                  bgcolor: tagStyle.bg,
                  color: tagStyle.color,
                  px: 2.5,
                  py: 1,
                  borderRadius: 1.5,
                  fontSize: "1rem",
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                {tagLabel}
              </Box>
            </Box>

            {/* 帳號狀態 */}
            <Box display="flex" alignItems="center" gap={0.75} mb={1}>
              <Icon sx={{ fontSize: 18, color: COLORS.grayDark }}>person_outline</Icon>
              <Typography variant="body2" color="text.secondary">
                {L.FieldStatus || "帳號狀態："}
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.main, fontWeight: 600 }}>
                {L.StatusActive || "正常"}
              </Typography>
            </Box>

            {/* Email */}
            <Box display="flex" alignItems="center" gap={0.75} mb={2}>
              <Icon sx={{ fontSize: 18, color: COLORS.grayDark }}>mail_outline</Icon>
              <Typography variant="body2" color="text.secondary">
                {L.FieldEmail || "Email："}{profile.email}
              </Typography>
            </Box>

            {/* 自動續費 toggle（2026-05-25 可互動）
                - TRIAL/FREE_TRIAL/FREE：disabled OFF（沒有訂閱可設）
                - 付費方案：可開關，關閉跳確認 modal
                - 2026-09-28 月繳期滿（綠界定期定額最後一期）：開關換成期滿日＋立即重新訂閱 */}
            {isFinalPeriod ? (
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    {(L.ContractEndLabel || "月繳訂閱將於 {date} 期滿（共 {n} 期）")
                      .replace("{date}", fmtDate(contract.endsAt))
                      .replace("{n}", String(contract.execTimes ?? "-"))}
                  </Typography>
                  <MDButton
                    size="small"
                    onClick={() => {
                      setResubMode("contract");
                      setResubscribeOpen(true);
                    }}
                    disabled={autoRenewLoading}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      minHeight: 0,
                      py: 0.5,
                      background: "linear-gradient(135deg, #ff8a3d 0%, #ffb347 100%)",
                      color: "#fff !important",
                      fontWeight: 600,
                      "&:hover": { background: "linear-gradient(135deg, #ff7a25 0%, #ffa030 100%)" },
                    }}
                  >
                    {L.ContractResubBtn || "立即重新訂閱"}
                  </MDButton>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, lineHeight: 1.6 }}>
                  {L.ContractEndHint || "這份訂閱的期數已全部扣完，期滿後不會再自動扣款、方案會切換為免費方案。如要繼續使用，請重新訂閱。"}
                </Typography>
              </Box>
            ) : (
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Typography variant="body2" color={isNoAutoRenew ? "text.disabled" : "text.secondary"}>
                  {L.FieldAutoRenew || "自動續費"}
                </Typography>
                <Switch
                  checked={!isNoAutoRenew && autoRenewEnabled}
                  disabled={autoRenewLoading}
                  onChange={handleAutoRenewChange}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: COLORS.main },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: COLORS.main,
                    },
                  }}
                />
              </Box>
            )}

            {/* 升級方案按鈕（PRO 不顯示）— 漸層 + 圓角 + 陰影 glow，對齊截圖二 */}
            {!isPro && (
              <MDButton
                fullWidth
                size="large"
                onClick={() => navigate("/plans")}
                sx={{
                  borderRadius: 3,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 700,
                  background: "linear-gradient(90deg, #ffd166 0%, #f88e2b 100%)",
                  color: "#fff !important",
                  boxShadow: "0 6px 16px rgba(248, 142, 43, 0.35)",
                  textTransform: "none",
                  "&:hover": {
                    background: "linear-gradient(90deg, #ffd166 0%, #f88e2b 100%)",
                    boxShadow: "0 8px 20px rgba(248, 142, 43, 0.45)",
                  },
                }}
              >
                {L.ActionUpgrade || "升級方案"}
              </MDButton>
            )}
          </Card>
        </Grid>

        {/* ───── 右卡：推薦碼 ───── */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, borderRadius: 3, boxShadow: 1, height: "100%", width: "100%" }}>
            {/* 推薦碼 */}
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <Box sx={{ width: 4, height: 18, bgcolor: COLORS.main, borderRadius: 1 }} />
              <MDTypography variant="subtitle1" fontWeight="bold">
                {L.FieldReferralCode || "推薦碼"}
              </MDTypography>
            </Box>
            <MDInput
              fullWidth
              value={profile.referralCode || ""}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        copyToClipboard(profile.referralCode, LANGUAGE.Alert?.Content2 || "已複製")
                      }
                      edge="end"
                      size="small"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3, ...FILLED_INPUT_SX }}
            />

            {/* 推薦網址 */}
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <Box sx={{ width: 4, height: 18, bgcolor: COLORS.main, borderRadius: 1 }} />
              <MDTypography variant="subtitle1" fontWeight="bold">
                {L.FieldReferralUrl || "推薦網址"}
              </MDTypography>
            </Box>
            <MDInput
              fullWidth
              value={referralUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        copyToClipboard(referralUrl, LANGUAGE.Alert?.Content3 || "已複製")
                      }
                      size="small"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => setQrOpen(true)}
                      edge="end"
                      size="small"
                      sx={{ ml: 0.5, bgcolor: "#e8eaed", borderRadius: 1 }}
                    >
                      <QrCodeIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={FILLED_INPUT_SX}
            />
          </Card>
        </Grid>
      </Grid>

      {/* ─── 關閉自動續費確認 Dialog（截圖三）─── */}
      <Dialog
        open={confirmOffOpen}
        onClose={() => !autoRenewLoading && setConfirmOffOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" px={3} py={2.5}>
          <MDTypography variant="h5" fontWeight="bold">
            {L.AutoRenewOffTitle || "關閉自動續費"}
          </MDTypography>
          <IconButton onClick={() => setConfirmOffOpen(false)} size="small" disabled={autoRenewLoading}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent dividers sx={{ px: 3, py: 3 }}>
          <MDTypography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.black, mb: 1 }}>
            {L.AutoRenewOffConfirmTitle || "確認關閉自動續費"}
          </MDTypography>
          <MDTypography sx={{ fontSize: 14, color: COLORS.main, lineHeight: 1.7 }}>
            {effectiveUntil
              ? (L.AutoRenewOffDescDated ||
                "關閉自動續費，將在 {date} 取消現有方案，確定關閉？").replace(
                  "{date}",
                  fmtDate(effectiveUntil)
                )
              : L.AutoRenewOffDesc ||
              "關閉自動續費，將在到期日取消現有方案，確定關閉？"}
          </MDTypography>
        </DialogContent>
        <Box display="flex" justifyContent="flex-end" gap={1.5} px={3} py={2}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setConfirmOffOpen(false)}
            disabled={autoRenewLoading}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            {L.Cancel || "取消"}
          </MDButton>
          <MDButton
            onClick={confirmTurnOffAutoRenew}
            disabled={autoRenewLoading}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              background: "linear-gradient(135deg, #ff8a3d 0%, #ffb347 100%)",
              color: "#fff !important",
              fontWeight: 600,
              "&:hover": { background: "linear-gradient(135deg, #ff7a25 0%, #ffa030 100%)" },
            }}
          >
            {autoRenewLoading ? (L.Processing || "處理中...") : (L.AutoRenewOffConfirm || "確認關閉")}
          </MDButton>
        </Box>
      </Dialog>

      {/* ─── 立即重新訂閱 Dialog（綠界已取消、無法無縫恢復時）─── */}
      <Dialog
        open={resubscribeOpen}
        onClose={() => !autoRenewLoading && setResubscribeOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" px={3} py={2.5}>
          <MDTypography variant="h5" fontWeight="bold">
            {resubMode === "contract"
              ? (L.ContractResubTitle || "重新訂閱以繼續使用")
              : (L.ResubTitle || "重新訂閱以開啟自動續費")}
          </MDTypography>
          <IconButton onClick={() => setResubscribeOpen(false)} size="small" disabled={autoRenewLoading}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent dividers sx={{ px: 3, py: 3 }}>
          <MDTypography sx={{ fontSize: 14, color: COLORS.black, lineHeight: 1.8 }}>
            {resubMode === "contract"
              ? (L.ContractResubDesc ||
                "您目前的月繳訂閱已是最後一期，期滿後不會再自動扣款。現在重新訂閱需完成付款：新訂閱立即生效、每月自動扣款直到您取消；目前方案剩餘的天數會換算成等值的 MAS Coin 回饋到您的帳戶，已付費的部分不會損失。您也可以等期滿、方案切換為免費方案後，再到方案頁重新訂閱。")
              : (L.ResubDesc ||
                "您的訂閱已進入取消程序，無法直接恢復自動續費。若要繼續使用，需要重新訂閱並完成付款。您目前方案剩餘的天數將換算成等值的 MAS Coin 回饋到您的帳戶，已付費的部分不會損失。完成付款後新訂閱立即生效並每月自動扣款。")}
          </MDTypography>
        </DialogContent>
        <Box display="flex" justifyContent="flex-end" gap={1.5} px={3} py={2}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setResubscribeOpen(false)}
            disabled={autoRenewLoading}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            {L.Cancel || "取消"}
          </MDButton>
          <MDButton
            onClick={confirmResubscribe}
            disabled={autoRenewLoading}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              background: "linear-gradient(135deg, #ff8a3d 0%, #ffb347 100%)",
              color: "#fff !important",
              fontWeight: 600,
              "&:hover": { background: "linear-gradient(135deg, #ff7a25 0%, #ffa030 100%)" },
            }}
          >
            {autoRenewLoading ? (L.Processing || "處理中...") : (L.ResubConfirm || "立即重新訂閱")}
          </MDButton>
        </Box>
      </Dialog>

      {/* ─── 年繳方案無法開啟自動續費 Dialog ─── */}
      <Dialog
        open={yearlyNoticeOpen}
        onClose={() => setYearlyNoticeOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" px={3} py={2.5}>
          <MDTypography variant="h5" fontWeight="bold">
            {L.AutoRenewYearlyTitle || "年繳方案無法開啟自動續費"}
          </MDTypography>
          <IconButton onClick={() => setYearlyNoticeOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent dividers sx={{ px: 3, py: 3 }}>
          <MDTypography sx={{ fontSize: 14, color: COLORS.black, lineHeight: 1.8 }}>
            {L.AutoRenewYearlyNotice ||
              "您目前是年繳方案，無法開啟月繳自動續費。年繳於到期前持續有效，到期後可再次購買續約。"}
          </MDTypography>
        </DialogContent>
        <Box display="flex" justifyContent="flex-end" px={3} py={2}>
          <MDButton
            onClick={() => setYearlyNoticeOpen(false)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              background: "linear-gradient(135deg, #ff8a3d 0%, #ffb347 100%)",
              color: "#fff !important",
              fontWeight: 600,
              "&:hover": { background: "linear-gradient(135deg, #ff7a25 0%, #ffa030 100%)" },
            }}
          >
            {L.AutoRenewYearlyBtn || "我知道了"}
          </MDButton>
        </Box>
      </Dialog>

      {/* ─── 編輯 Dialog ─── */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" px={3} py={2.5}>
          <MDTypography variant="h5" fontWeight="bold">
            {L.DialogEditTitle || "編輯"}
          </MDTypography>
          <IconButton onClick={() => setEditOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent dividers sx={{ px: 3, py: 3 }}>
          <Typography variant="caption" fontWeight="medium" sx={{ display: "block", mb: 0.75 }}>
            {L.DialogEditMemberName || "會員名稱"}
            <span style={{ color: COLORS.error }}>*</span>
          </Typography>
          <TextField
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: COLORS.grayLightest,
                borderRadius: 1.5,
                "& fieldset": { border: "none" },
              },
              "& .MuiOutlinedInput-input": { padding: "12px 14px", fontSize: 14 },
            }}
          />
        </DialogContent>
        <Box display="flex" justifyContent="flex-end" gap={1.5} px={3} py={2}>
          <MDButton
            variant="outlined"
            onClick={() => setEditOpen(false)}
            sx={{ textTransform: "none", borderRadius: 2, borderColor: COLORS.grayMedian, color: COLORS.black }}
          >
            {L.DialogEditCancel || "取消"}
          </MDButton>
          <MDButton
            onClick={handleEditConfirm}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              background: "linear-gradient(90deg, #f88e2b 0%, #ffd166 100%)",
              color: COLORS.white,
              "&:hover": {
                background: "linear-gradient(90deg, #f88e2b 0%, #ffd166 100%)",
              },
            }}
          >
            {L.DialogEditConfirm || "確認"}
          </MDButton>
        </Box>
      </Dialog>

      {/* ─── QR Code Dialog ─── */}
      <Dialog
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" px={3} py={2.5}>
          <MDTypography variant="h5" fontWeight="bold">
            {L.CardReferral || "推薦"}
          </MDTypography>
          <IconButton onClick={() => setQrOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent dividers sx={{ textAlign: "center", py: 4 }}>
          <Box display="flex" justifyContent="center">
            <QRCodeCanvas
              ref={qrRef}
              value={referralUrl}
              size={220}
              bgColor={COLORS.white}
              fgColor={COLORS.black}
              level="H"
              includeMargin
            />
          </Box>
        </DialogContent>
        <Box px={3} py={2}>
          <MDButton
            fullWidth
            size="large"
            onClick={() => {
              const canvas = qrRef.current;
              if (!canvas) return;
              const pngUrl = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");
              const a = document.createElement("a");
              a.href = pngUrl;
              a.download = `MAS_Referral_QR_${profile.referralCode}.png`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }}
            sx={{
              borderRadius: 2,
              background: "linear-gradient(90deg, #f88e2b 0%, #ffd166 100%)",
              color: COLORS.white,
              "&:hover": { background: "linear-gradient(90deg, #f88e2b 0%, #ffd166 100%)" },
            }}
          >
            {L.DownloadQrcode || "下載 Qrcode"}
          </MDButton>
        </Box>
      </Dialog>
    </MDBox>
  );
}
